import React from 'react';
import { compose, omit, path, pick, prop, identity } from 'ramda';
import { Image } from '@core/js/components/common/Image';
import { useLiveGameConfig, useGameImageUrls } from '@core/js/hooks/games';
import { withTranslations } from '@core/js/components/hocs/withTranslations';
import { AccessibilityElement } from '@core/js/components/common/AccessibilityElement';
import { useAppSelector, useDeviceSelector, useUserSelector } from '@core/js/store/hooks';
import { branch, mapProps, pure, renderNothing, withProps } from 'recompose';
import InfoIcon from '@theme/js/components/common/Game/InfoIcon';
import { Icon } from '@core/js/components/common/Icon';

import FeatureIcons from '@core/js/components/common/Game/FeatureIcons';
import { FavoriteIcon } from '@core/js/components/common/Game/FavoriteIcon';
/* TotalCasino changes - categoryName support */
import withGameLaunch from '@theme/js/components/common/Game/withGameLaunch';
import { withGameInfo } from '@core/js/components/common/Game/withGameInfo';

import style from './Game.scss';

export const GameBare = ({
    t,
    code,
    name,
    size = 'extraSmall',
    showTitle = false,
    showFeatureIcons = true,
    gameFeed,
    showFavoriteIcon,
    showRecentlyPlayedIcon,
    className = '',
    setImageRef,
    setGameRef,
    setGameImageRef,
    getGameInfoPath,
    launchGame,
    disableGameLaunch,
    onMouseMove,
    isNew,
    hot,
    popular,
    exclusive,
    liveLaunchAlias,
    includeLiveGames = false,
    categoryName,
    isBlocked
    }) => {
    const liveGameConfig = useLiveGameConfig(liveLaunchAlias);
    const isLiveGame = includeLiveGames && liveGameConfig.launchAlias;
    const { dynamicImageUrl, limits, isScheduled, openingDateTime, dealerName, language, seatsInfo } = liveGameConfig;
    const isScheduledLiveGame = isLiveGame && isScheduled;
    const imageUrls = useGameImageUrls({ code, dynamicImageUrl, isLiveGame });
    const gameInfoPath = getGameInfoPath();

    const gamesConfiguration = useAppSelector(prop('gamesConfiguration'));
    const assets = gamesConfiguration[code].additionalAssets[0];
    const bg = assets?.url ? { backgroundImage: `url("${assets.url}")` } : null;
    const tKey = `playtech.mobile.system.blocked.${assets?.name || 'default'}`;

    const onClickLaunchGame = (event) => {
        if (isBlocked) {
            return;
        }

        const onGameClickLaunch = disableGameLaunch ? identity : launchGame;

        event.preventDefault();
        onGameClickLaunch();
    };

    return (
        <AccessibilityElement
            data-game-code={code}
            elementRef={setGameRef}
            className={`${style.container} ${style[size]} ${className} ${isBlocked ? style.blockedGame : ''}`}
            aria-label={`${t('playtech.mobile.system.game.aria-label')} ${name}`}
            /* TotalCasino changes - categoryName support */
            data-game-category={categoryName}
        >
            {isBlocked && (
                <>
                    <Icon className={style.blockedGameIcon} style={bg} />
                    <span className={style.blockedGameText}>{t(tKey)}</span>
                </>
            )}
            <div
                onClick={onClickLaunchGame}
                aria-hidden={true}
                ref={setGameImageRef}
                onMouseMove={onMouseMove}
                className={style.imageContainer}
            >
                <Image
                    alt={name}
                    src={imageUrls}
                    ref={setImageRef}
                    className={style.image}
                />
                {isScheduledLiveGame && !isBlocked && <div className={style.scheduledLiveGameBackground} />}
                {(showFeatureIcons || showFavoriteIcon || isBlocked) && <div className={isBlocked ? `${style.background} ${style.blockedGame}` : style.background} />}
                {showRecentlyPlayedIcon && !isBlocked && <div className={style.recentlyPlayedIcon} />}
            </div>
            {!showRecentlyPlayedIcon && !isBlocked &&
                <InfoIcon gameInfoPath={gameInfoPath} />
            }
            <div className={`${style.sideLabels} ${showTitle ? style.withTitle : ''}`} aria-hidden={true}>
                {showFeatureIcons && !isBlocked && (
                    <div className={style.labels}>
                        {isNew && <span className={style.label}>{t('playtech.mobile.responsivegames.game.label.new')}</span>}
                        {popular && <span className={style.label}>{t('playtech.mobile.responsivegames.game.label.popular')}</span>}
                        {hot && <span className={style.label}>{t('playtech.mobile.responsivegames.game.label.hot')}</span>}
                        {exclusive && <span className={style.label}>{t('playtech.mobile.responsivegames.game.label.exclusive')}</span>}
                    </div>
                )}
                {isLiveGame && limits && !isBlocked && <div className={style.liveLimits}>{limits}</div>}
            </div>
            {isScheduledLiveGame && !isBlocked && (
                <div className={`${style.scheduledLiveGameInfo} ${showTitle ? style.withTitle : ''}`} aria-hidden={true}>
                    {openingDateTime
                        ? (
                            <>
                                <span>{t('playtech.mobile.system.liveGame.startsAt')}</span>
                                <span>{openingDateTime}</span>
                            </>
                        )
                        : <span>{t('playtech.mobile.system.liveGame.startsSoon')}</span>
                    }
                </div>
            )}
            {!isBlocked && (
                <div className={`${style.sideIcons} ${showTitle ? style.withTitle : ''}`}>
                    {isLiveGame && (
                        <div className={style.liveInfo} aria-hidden={true}>
                            <span>{dealerName}</span>
                            <span>{language}</span>

                            {showFeatureIcons && (
                                <FeatureIcons
                                    code={code}
                                    noContainer={true}
                                    aria-hidden={true}
                                    classNames={{ item: style.featured }}
                                    types={['goldenChips', 'wilds', 'fsb']}
                                />
                            )}
                        </div>
                    )}
                    {showFeatureIcons && !isLiveGame && (
                        <>
                            <FeatureIcons
                                code={code}
                                noContainer={true}
                                aria-hidden={true}
                                classNames={{ item: style.featured }}
                                types={['goldenChips', 'wilds', 'fsb']}
                            />
                        </>
                    )}
                    {!showTitle && showFavoriteIcon && !isLiveGame && (
                        <FavoriteIcon
                            code={code}
                            gameFeed={gameFeed}
                            className={style.favorite}
                        />
                    )}
                    {isLiveGame && (
                        <div className={style.seatsInfo} aria-hidden={true}>
                            <span>{seatsInfo}</span>
                            <span className={style.seatsIcon} />
                        </div>
                    )}
                </div>
            )}
            {showTitle && <div className={style.titleWrapper}>
                <span className={`${style.name} ${style.ellipsis}`} aria-hidden={true}>{name}</span>
            </div>}
        </AccessibilityElement>
    );
};

export const isGameAvailable = compose(
    withProps(({ code }) => ({ isAvailable: Boolean(useAppSelector(path(['games', code]))) })),
    branch(
        props => !props.isAvailable,
        renderNothing
    ),
    mapProps(omit(['isAvailable']))
);

export const enhance = compose(
    pure,
    withTranslations,
    isGameAvailable,
    withGameInfo,
    withGameLaunch,
    withProps(({ code }) => ({
        isDesktop: useDeviceSelector(prop('isDesktop')),
        ...useAppSelector(path(['games', code])),
        ...useUserSelector(pick(['isLoggedIn']))
    })),
    mapProps(omit(['description', 'funSupport', 'jackpotCode']))
);

export const Game = enhance(GameBare);
